import React, { useEffect, useState } from "react";
import { format, parseISO } from "date-fns";

import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import Drawer from "@material-ui/core/Drawer";
import Link from "@material-ui/core/Link";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import Box from "@material-ui/core/Box";
import Divider from "@material-ui/core/Divider";
import CircularProgress from "@material-ui/core/CircularProgress";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import withMediaToken from "../../services/mediaUrl";
import { emitClientUpdated, onClientUpdated } from "../../services/clientEvents";
import ContactModal from "../ContactModal";
import ClientModal from "../ClientModal";
import ContactDrawerSkeleton from "../ContactDrawerSkeleton";
import StatusChip from "../StatusChip";

const drawerWidth = 320;

const useStyles = makeStyles(theme => ({
	drawer: {
		width: drawerWidth,
		flexShrink: 0,
	},
	drawerPaper: {
		width: drawerWidth,
		display: "flex",
		borderTop: "1px solid rgba(0, 0, 0, 0.12)",
		borderRight: "1px solid rgba(0, 0, 0, 0.12)",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		borderTopRightRadius: 4,
		borderBottomRightRadius: 4,
	},
	header: {
		display: "flex",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		backgroundColor: "#eee",
		alignItems: "center",
		padding: theme.spacing(0, 1),
		minHeight: "73px",
		justifyContent: "flex-start",
	},
	content: {
		display: "flex",
		backgroundColor: "#eee",
		flexDirection: "column",
		padding: "8px 8px 8px 8px",
		height: "100%",
		overflowY: "auto",
		gap: 8,
		...theme.scrollbarStyles,
	},

	contactAvatar: {
		margin: 8,
		width: 96,
		height: 96,
	},

	contactHeader: {
		display: "flex",
		padding: 12,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},
	contactName: {
		fontWeight: 600,
		marginTop: 4,
		textAlign: "center",
	},
	contactInfoList: {
		marginTop: 12,
		width: "100%",
		display: "flex",
		flexDirection: "column",
		gap: 6,
	},
	contactActions: {
		marginTop: 12,
		display: "flex",
		gap: 8,
		flexWrap: "wrap",
		justifyContent: "center",
	},

	section: {
		padding: 12,
		display: "flex",
		flexDirection: "column",
		gap: 8,
	},
	sectionTitle: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		marginBottom: 4,
	},
	label: {
		fontSize: 11,
		color: "#777",
		textTransform: "uppercase",
		fontWeight: 600,
	},
	value: {
		fontSize: 13,
		wordBreak: "break-word",
	},
	chipRow: {
		display: "flex",
		flexWrap: "wrap",
		gap: 4,
		marginTop: 2,
	},
	statusChip: {
		color: "#fff",
		fontWeight: 600,
	},
	activityItem: {
		borderLeft: "3px solid #1976d2",
		paddingLeft: 10,
		marginBottom: 10,
	},
}));

const displayName = c => {
	if (!c) return "";
	if (c.personType === "PJ") return c.tradeName || c.companyName || "(Sem nome)";
	return [c.firstName, c.lastName].filter(Boolean).join(" ") || "(Sem nome)";
};

const displayDoc = c => (c?.personType === "PJ" ? c?.cnpj : c?.cpf);

const ACTION_LABELS = {
	"client.created": "Cliente cadastrado",
	"client.fields_updated": "Campos atualizados",
	"client.status_changed": "Status alterado",
	"client.source_changed": "Origem alterada",
	"client.tags_updated": "Tags atualizadas",
	"client.custom_fields_updated": "Campos personalizados atualizados",
};

const renderValue = v => {
	if (v === null || v === undefined || v === "") return '"(vazio)"';
	return `"${v}"`;
};

const ActivityItem = ({ activity, classes }) => {
	const label = ACTION_LABELS[activity.action] || activity.action;
	const ch = activity.changes || {};
	const ts = activity.createdAt
		? format(parseISO(activity.createdAt), "dd/MM/yyyy HH:mm")
		: "";

	let title = label;
	let lines = [];

	if (activity.action === "client.status_changed") {
		title = `Status: ${ch.from?.name || "-"} → ${ch.to?.name || "-"}`;
	} else if (activity.action === "client.source_changed") {
		title = `Origem: ${ch.from?.name || "-"} → ${ch.to?.name || "-"}`;
	} else if (activity.action === "client.tags_updated") {
		title = "Tags atualizadas";
		if (ch.added?.length)
			lines.push(`+ ${ch.added.map(t => t.name).join(", ")}`);
		if (ch.removed?.length)
			lines.push(`- ${ch.removed.map(t => t.name).join(", ")}`);
	} else if (activity.action === "client.fields_updated") {
		title = "Campos atualizados";
		lines = (ch.fields || []).map(
			f => `${f.label}: ${renderValue(f.from)} → ${renderValue(f.to)}`
		);
	} else if (activity.action === "client.custom_fields_updated") {
		title = "Campos personalizados";
		lines = (ch.fields || []).map(
			f => `${f.name}: ${renderValue(f.from)} → ${renderValue(f.to)}`
		);
	}

	return (
		<Box className={classes.activityItem}>
			<Typography variant="body2" style={{ fontWeight: 600, fontSize: 12 }}>
				{title}
			</Typography>
			{lines.map((l, i) => (
				<Typography
					key={i}
					variant="body2"
					style={{ color: "#555", fontSize: 12 }}
				>
					{l}
				</Typography>
			))}
			<Typography variant="caption" style={{ color: "#888" }}>
				{ts}
				{activity.user?.name ? ` · ${activity.user.name}` : ""}
			</Typography>
		</Box>
	);
};

const ClientPanel = ({ clientId, classes }) => {
	const [client, setClient] = useState(null);
	const [loading, setLoading] = useState(false);
	const [editOpen, setEditOpen] = useState(false);
	const [activities, setActivities] = useState([]);
	const [loadingActivities, setLoadingActivities] = useState(false);

	const fetchClient = async () => {
		setLoading(true);
		try {
			const { data } = await api.get(`/clients/${clientId}`);
			setClient(data);
		} catch (err) {
			toastError(err);
		} finally {
			setLoading(false);
		}
	};

	const fetchActivities = async () => {
		setLoadingActivities(true);
		try {
			const { data } = await api.get(`/clients/${clientId}/activities`, {
				params: { pageNumber: 1 },
			});
			setActivities(data.activities || []);
		} catch (err) {
			/* silent */
		} finally {
			setLoadingActivities(false);
		}
	};

	useEffect(() => {
		if (!clientId) return;
		fetchClient();
		fetchActivities();
		// eslint-disable-next-line
	}, [clientId]);

	useEffect(() => {
		if (!clientId) return undefined;
		return onClientUpdated((updated) => {
			if (updated && updated.id === clientId) {
				setClient((prev) => ({ ...(prev || {}), ...updated }));
				fetchActivities();
			}
		});
		// eslint-disable-next-line
	}, [clientId]);

	if (!clientId) return null;
	if (loading && !client) {
		return (
			<Paper square variant="outlined" className={classes.section}>
				<CircularProgress size={20} />
			</Paper>
		);
	}
	if (!client) return null;

	const phone = client.mobilePhone || client.phone;

	return (
		<>
			<Paper square variant="outlined" className={classes.section}>
				<div className={classes.sectionTitle}>
					<Typography variant="subtitle2">Cliente vinculado</Typography>
					<IconButton
						size="small"
						title="Editar cadastro completo"
						onClick={() => setEditOpen(true)}
					>
						<OpenInNewIcon fontSize="small" />
					</IconButton>
				</div>

				<div>
					<div className={classes.label}>Nome</div>
					<div className={classes.value}>{displayName(client)}</div>
				</div>

				<div>
					<div className={classes.label}>Tipo</div>
					<div className={classes.value}>
						{client.personType === "PJ" ? "Pessoa Jurídica" : "Pessoa Física"}
					</div>
				</div>

				<div>
					<div className={classes.label}>CPF/CNPJ</div>
					<div className={classes.value}>{displayDoc(client) || "-"}</div>
				</div>

				{client.personType === "PJ" && (
					<>
						<div>
							<div className={classes.label}>Razão Social</div>
							<div className={classes.value}>{client.companyName || "-"}</div>
						</div>
						<div>
							<div className={classes.label}>Nome Fantasia</div>
							<div className={classes.value}>{client.tradeName || "-"}</div>
						</div>
					</>
				)}

				<div>
					<div className={classes.label}>Status</div>
					<div className={classes.chipRow}>
						{client.clientStatus ? (
							<StatusChip
								label={client.clientStatus.name}
								color={client.clientStatus.color}
							/>
						) : (
							<span className={classes.value}>-</span>
						)}
					</div>
				</div>

				<div>
					<div className={classes.label}>E-mail</div>
					<div className={classes.value}>
						{client.email ? (
							<Link href={`mailto:${client.email}`}>{client.email}</Link>
						) : (
							"-"
						)}
					</div>
				</div>

				<div>
					<div className={classes.label}>Telefone</div>
					<div className={classes.value}>
						{phone ? <Link href={`tel:${phone}`}>{phone}</Link> : "-"}
					</div>
				</div>

				<div>
					<div className={classes.label}>Origem</div>
					<div className={classes.chipRow}>
						{client.source ? (
							<StatusChip
								label={client.source.name}
								color={client.source.color}
							/>
						) : (
							<span className={classes.value}>-</span>
						)}
					</div>
				</div>

				<div>
					<div className={classes.label}>Tags</div>
					<div className={classes.chipRow}>
						{(client.tags || []).length === 0 && (
							<span className={classes.value}>-</span>
						)}
						{(client.tags || []).map(tag => (
							<StatusChip key={tag.id} label={tag.name} color={tag.color} />
						))}
					</div>
				</div>

				{(client.extraInfo || []).length > 0 && (
					<>
						<Divider />
						<div className={classes.label}>Campos personalizados</div>
						{client.extraInfo.map(info => (
							<div key={info.id || info.name}>
								<div className={classes.label}>{info.name}</div>
								<div className={classes.value}>
									{info.value && info.value !== "" ? info.value : "-"}
								</div>
							</div>
						))}
					</>
				)}

				{client.notes && (
					<>
						<Divider />
						<div>
							<div className={classes.label}>Observações</div>
							<div
								className={classes.value}
								style={{ whiteSpace: "pre-wrap" }}
							>
								{client.notes}
							</div>
						</div>
					</>
				)}
			</Paper>

			<Paper square variant="outlined" className={classes.section}>
				<Typography variant="subtitle2">
					Histórico de atividades
				</Typography>
				{loadingActivities && activities.length === 0 && (
					<CircularProgress size={18} />
				)}
				{!loadingActivities && activities.length === 0 && (
					<Typography variant="body2" style={{ color: "#888", fontSize: 12 }}>
						Nenhuma atividade registrada.
					</Typography>
				)}
				{activities.map(a => (
					<ActivityItem key={a.id} activity={a} classes={classes} />
				))}
			</Paper>

			<ClientModal
				open={editOpen}
				onClose={() => setEditOpen(false)}
				clientId={client.id}
				onSave={saved => {
					setClient(prev => ({ ...prev, ...saved }));
					fetchClient();
					fetchActivities();
					emitClientUpdated(saved);
				}}
			/>
		</>
	);
};

const ContactDrawer = ({ open, handleDrawerClose, contact, loading }) => {
	const classes = useStyles();

	const [modalOpen, setModalOpen] = useState(false);

	return (
		<Drawer
			className={classes.drawer}
			variant="persistent"
			anchor="right"
			open={open}
			PaperProps={{ style: { position: "absolute" } }}
			BackdropProps={{ style: { position: "absolute" } }}
			ModalProps={{
				container: document.getElementById("drawer-container"),
				style: { position: "absolute" },
			}}
			classes={{
				paper: classes.drawerPaper,
			}}
		>
			<div className={classes.header}>
				<IconButton onClick={handleDrawerClose}>
					<CloseIcon />
				</IconButton>
				<Typography style={{ justifySelf: "center" }}>
					{i18n.t("contactDrawer.header")}
				</Typography>
			</div>
			{loading ? (
				<ContactDrawerSkeleton classes={classes} />
			) : (
				<div className={classes.content}>
					<Paper square variant="outlined" className={classes.contactHeader}>
						<Avatar
							alt={contact.name}
							src={withMediaToken(contact.profilePicUrl)}
							className={classes.contactAvatar}
						/>
						<Typography variant="subtitle1" className={classes.contactName}>
							{contact.name}
						</Typography>
						{contact.isGroup && (
							<Chip size="small" label="Grupo" style={{ marginTop: 4 }} />
						)}

						<div className={classes.contactInfoList}>
							<div>
								<div className={classes.label}>Número</div>
								<div className={classes.value}>
									<Link href={`tel:${contact.number}`}>
										{contact.number || "-"}
									</Link>
								</div>
							</div>
							{contact.email && (
								<div>
									<div className={classes.label}>E-mail</div>
									<div className={classes.value}>
										<Link href={`mailto:${contact.email}`}>
											{contact.email}
										</Link>
									</div>
								</div>
							)}
							{contact.createdAt && (
								<div>
									<div className={classes.label}>Cadastrado em</div>
									<div className={classes.value}>
										{new Date(contact.createdAt).toLocaleString("pt-BR")}
									</div>
								</div>
							)}
							{contact.extraInfo && contact.extraInfo.length > 0 && (
								<>
									<Divider />
									{contact.extraInfo.map(info => (
										<div key={info.id || info.name}>
											<div className={classes.label}>{info.name}</div>
											<div className={classes.value}>
												{info.value || "-"}
											</div>
										</div>
									))}
								</>
							)}
						</div>

						<div className={classes.contactActions}>
							<Button
								variant="outlined"
								color="primary"
								size="small"
								onClick={() => setModalOpen(true)}
							>
								{i18n.t("contactDrawer.buttons.edit")}
							</Button>
						</div>
						<ContactModal
							open={modalOpen}
							onClose={() => setModalOpen(false)}
							contactId={contact.id}
						/>
					</Paper>

					<ClientPanel clientId={contact.clientId} classes={classes} />
				</div>
			)}
		</Drawer>
	);
};

export default ContactDrawer;
